import { useCallback } from 'react';

import { ProgressBar } from '../../components/ProgressBar';
import { Skeleton } from '../../components/Skeleton';
import { Pressable } from '../../primitives/Pressable';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { brandColor } from '../../styles/brand-colors';
import { DARK_LAYER1, DARK_LIFTED } from '../../styles/elevation';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { restyleKey } from '../../utils/restyleKey';
import type { MaterialTileProps } from './DeckMaterials.types';
import { DECK_MATERIAL_ICONS } from './icons';

/**
 * One card of the "All Sources" grid (Figma `1:16006`): a portrait frame
 * (171:241, radius 26) holding the preview, a kind badge top-left, an actions
 * menu top-right, and the name (+ optional caption) underneath. The whole
 * frame opens the item; the menu is where renaming and removing live.
 *
 * The preview is a slot: a cached thumbnail when the platform has one, a
 * miniature for a note or a map, never a live document renderer (a per-tile
 * PDF view is the memory failure mode on both platforms).
 *
 * A material with no cover shows the EMPTY FRAME — its own fill, its border
 * and the kind badge, nothing else. No oversized glyph behind the content:
 * a placeholder that large reads as a broken image rather than as an empty
 * one, and it sat under real covers too, showing through anything that did
 * not fill the frame. What a tile with no cover is saying belongs in its
 * caption ("Not generated"), where a reader is already looking.
 *
 * A document still on its way in shows a STATUS line in the caption's place:
 * how far its upload has got (a bar and its percentage) when the platform
 * measures it, else "Uploading" with a spinner — or the failure with Retry.
 * The tile is where the user is looking, so the tile says what is happening
 * to it. While it uploads, its empty frame is the app's fading skeleton — the
 * one loading state — rather than a blank card. It does not open until it has
 * landed (Retry still presses).
 *
 * A coverless frame is filled one step DOWN the ramp from one with a cover.
 * Web's grid sits on a tinted mat, so a white card reads as a card there —
 * but the phone's sits on the app background, where an empty tile would be
 * near-white on near-white and carried by a hairline alone. The deeper fill
 * makes it read as a deliberate empty slot on both, and it can only apply
 * where there is nothing to show through it.
 */
export function MaterialTile({
  item,
  onOpen,
  actionsSlot,
  preview,
  caption,
  status,
  frameHeight,
  revealActionsOnHover = false,
  className,
  testID,
}: MaterialTileProps) {
  const { isDark } = useTheme();
  const Icon = DECK_MATERIAL_ICONS[item.kind];
  const badgeIconColor = brandColor('secondary', '700', isDark);

  const open = useCallback(() => onOpen(item.ref), [onOpen, item.ref]);

  const frameSize = frameHeight != null ? { height: frameHeight } : undefined;

  return (
    <View
      className={cn('group relative w-full flex-col items-center gap-1.5', className)}
      testID={testID}>
      <Pressable
        // Nothing to open until it is in the deck: a tile with a status (on its
        // way in, or failed) does not press, and wears the DS disabled dim.
        // Keyed on it, so landing remounts the frame rather than flipping its
        // dim class in place (`flashka-native/no-runtime-class-toggle`).
        key={restyleKey('frame', !!status)}
        disabled={!!status}
        onPress={open}
        accessibilityRole="button"
        accessibilityLabel={item.label}
        className={cn(
          'relative w-full overflow-hidden rounded-[26px] border border-secondary-200',
          // A preview tile is a white sheet on the grey pane in light. In dark
          // a tile adds no layer to the pane (elevation.ts): the preview fills
          // it, the hairline is its edge, and a coverless tile lifts one step
          // only under the pointer.
          preview
            ? 'bg-surface-raised'
            : cn('bg-secondary-100', DARK_LAYER1, 'dark:web:hover:bg-secondary-100'),
          frameHeight == null && 'web:aspect-[171/241]',
        )}
        webProps={frameSize ? { style: frameSize } : undefined}
        nativeProps={frameSize ? { style: frameSize } : undefined}
        testID={testID ? `${testID}-frame` : undefined}>
        {preview ? <View className="absolute inset-0">{preview}</View> : null}
        {!preview && status?.kind === 'uploading' ? (
          <View
            className="absolute inset-0"
            testID={testID ? `${testID}-loading` : undefined}>
            <Skeleton className="h-full w-full rounded-none" />
          </View>
        ) : null}

        {/* Kind badge — the same glyph the segment and the "+" row use. */}
        <View
          className={cn(
            'absolute left-[10px] top-[10px] size-8 items-center justify-center rounded-[10px] border border-secondary-100 bg-secondary-50/70',
            // Dark: the lifted step, opaque — it sits over the preview (often a
            // white page), where a see-through chip lost its light glyph.
            DARK_LIFTED,
          )}
          testID={testID ? `${testID}-kind` : undefined}>
          <Icon color={badgeIconColor} size={16} />
        </View>
      </Pressable>

      {/* OUTSIDE the frame, overlaying it: the frame is itself a button, and a
          button inside a button is invalid HTML — the browser re-parents it,
          which breaks both the click target and the accessibility tree. The
          group class stays on the frame's parent so hover still reveals it.

          Hover-revealed on web, ALWAYS VISIBLE on touch: a phone has no hover,
          so a menu that waits for one is a menu that does not exist. */}
      {actionsSlot ? (
        <View
          className={cn(
            'absolute right-2 top-2',
            revealActionsOnHover &&
              'web:opacity-0 web:transition-opacity web:group-hover:opacity-100 web:focus-within:opacity-100',
          )}>
          {actionsSlot}
        </View>
      ) : null}

      <View className="w-full flex-col items-center">
        {/* Explicit ramp on each Text: native does not inherit type through a View. */}
        <Text
          numberOfLines={1}
          className="w-full text-center text-h4 font-semibold text-secondary-600 native:font-mona-semibold">
          {item.label}
        </Text>
        {status ? (
          <View
            className="h-[22px] w-full flex-row items-center justify-center gap-1.5"
            testID={testID ? `${testID}-status` : undefined}>
            {status.kind === 'uploading' ? (
              // The bar alone (the upload rule): real progress when the
              // platform reports it, the travelling segment when it does not —
              // no spinner, no percentage, no "Uploading…" word under the name.
              <ProgressBar
                live
                indeterminate={status.progress == null}
                value={status.progress ?? 0}
                accessibilityLabel={status.label}
                className="px-3"
                testID={testID ? `${testID}-progress` : undefined}
              />
            ) : (
              <>
                <Text
                  numberOfLines={1}
                  className="text-center text-b2 font-medium text-red-600 native:font-mona-medium">
                  {status.label}
                </Text>
                <Pressable
                  onPress={status.onRetry}
                  accessibilityRole="button"
                  accessibilityLabel={status.retryLabel}
                  testID={testID ? `${testID}-retry` : undefined}>
                  <Text className="text-b2 font-semibold text-primary-600 native:font-mona-semibold">
                    {status.retryLabel}
                  </Text>
                </Pressable>
              </>
            )}
          </View>
        ) : caption ? (
          <Text
            numberOfLines={1}
            className="w-full text-center text-b2 font-medium text-secondary-400 native:font-mona-medium">
            {caption}
          </Text>
        ) : null}
      </View>
    </View>
  );
}
(MaterialTile as { displayName?: string }).displayName = 'MaterialTile';
